import { useProjectParams } from '@hooks/useProjectParams';
import { AgGridTable } from 'components/AgGrid/AgGridTable';
import { ConfirmationDialog } from 'components/ConfirmationDialog';
import { getColumnId, getColumnsWithPinnedProp, onTableSettingsConfirm, TableSettings } from 'components/TableSettings';
import { PriceListTableCount } from 'components/TotalCount/PriceListTableCount';
import { RequestStatus } from 'features/request/requestSlice';
import { useRequest } from 'features/request/useRequest';
import { useResetMultipleStoreData } from 'features/request/useResetStoreData';
import type { PriceListMtrRowData } from 'gql/graphql';
import { ContractStatusEnum, ProjectStatusEnum } from 'gql/graphql';
import { useAccess } from 'hooks/useAccess';
import { useMethodsConfirmationDialog } from 'hooks/useMethodsConfirmationDialog';
import { usePin } from 'hooks/usePin';
import { useProjectData } from 'hooks/useProjectData';
import { useSelection } from 'hooks/useSelection';
import { PageHeaderLayoutContext } from 'pages/Project/Common/Layouts/PageLayoutContext';
import { ProjectPageLayout } from 'pages/Project/Common/Layouts/ProjectPageLayout';
import { Button, Grid, GridItem, Layout } from 'uikit';

import type { ColDef, ColGroupDef, GridReadyEvent } from 'ag-grid-community';
import type { AgGridReact } from 'ag-grid-react';
import cn from 'classnames';
import type { CSSProperties } from 'react';
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Route, Routes } from 'react-router-dom';

import { EditMtrDialog } from '../EditMtrDialog';

import { usePriceListColumns } from './mtrColumns';
import { priceListMtrRemoveUnusedRows } from './mtrQueries';
import { usePriceListMtr } from './usePriceListMtr';

import Styles from './PriceListMtr.module.css';

export const PriceListMtr = () => {
    const { id: projectId, methodology } = useProjectParams();
    const [position, setPosition] = useState<number | undefined>();

    const { projectData } = useProjectData(projectId);
    const contractStatus = projectData?.contractNavigation?.data?.contractNavData?.status;
    const dispatch = useDispatch();

    const { isHeaderOpened } = useContext(PageHeaderLayoutContext);

    const { priceList: priceListRules } = useAccess();

    const ref = useRef<AgGridReact<PriceListMtrRowData>>(null);
    const tableSettingsRef = useRef<HTMLDivElement>(null);

    const { columns, filterValues, resetStatusFilter } = usePriceListColumns({
        contractId: projectData?.contractId,
        contractStatus,
    });

    const { datasource, totalCount, fetchedCount, refreshData } = usePriceListMtr({
        contractId: projectData?.contractId,
        filterValues,
        gridRef: ref,
    });

    useEffect(() => {
        if (typeof ref !== 'function' && ref?.current) {
            if (!totalCount) {
                ref?.current?.api?.showNoRowsOverlay();
            } else {
                ref?.current?.api?.hideOverlay();
            }
        }
    }, [totalCount, ref]);

    const [selection, , , setSelection] = useSelection('PriceListMtrTable');
    const [pinnedItems, , , setPinned] = usePin('PriceListMtrTable');

    const isColumnVisible = useCallback((id: string) => selection[id] ?? true, [selection]);

    const contractIdVariable = useMemo(
        () => ({
            contractId: projectData?.contractId,
        }),
        [projectData?.contractId],
    );

    const [, removeUnusedMtrStatus, , removeUnusedMtrRowsRequest] = useRequest({
        requestId: 'priceListMtrRemoveUnusedRows',
        query: priceListMtrRemoveUnusedRows,
        variables: contractIdVariable,
        withEffect: false,
    });

    const { isOpenConfirmationDialog, handleOpenConfirmationDialog, handleCloseConfirmationDialog, handleActionQuery } =
        useMethodsConfirmationDialog({
            actionQuery: removeUnusedMtrRowsRequest,
            variables: contractIdVariable,
        });

    const memoizedRequestIds = useMemo(
        () => [
            'uerPriceListMtrCodeFilter',
            'uerPriceListMtrNameFilter',
            'uerPriceListMtrStatusFilter',
            'uerPriceListMtrSupplyByFilter',
            'uerPriceListMtrTypeFilter',
            'uerPriceListMtrJustificationFilter',
        ],
        [],
    );

    const [resetPageData] = useResetMultipleStoreData({
        requestIds: memoizedRequestIds,
    });

    useEffect(
        () => () => {
            resetPageData();
        },
        [resetPageData],
    );

    useEffect(() => {
        if (removeUnusedMtrStatus === RequestStatus.SUCCESS) {
            refreshData();
        }
    }, [dispatch, refreshData, removeUnusedMtrStatus]);

    const updateMtrHandler = useCallback(() => {
        if (projectData?.contractId) {
            refreshData();
            resetStatusFilter(undefined);
        }
    }, [projectData?.contractId, refreshData, resetStatusFilter]);

    const breadCrumbsMtrItems = useMemo(
        () => [
            {
                label: projectData?.name,
                href: `/projects/${methodology}/${projectData?.uniqueId}/`,
            },
            {
                label: 'Договор',
                href: `/projects/${methodology}/${projectData?.uniqueId}/contract`,
            },
        ],
        [methodology, projectData?.uniqueId, projectData?.name],
    );

    const fullSetColumns = useMemo<(ColDef | ColGroupDef)[]>(() => {
        const fullSetColumnsWithPins = getColumnsWithPinnedProp({ columns, pinnedItems });

        return fullSetColumnsWithPins.filter((column) => isColumnVisible(getColumnId(column)));
    }, [columns, isColumnVisible, pinnedItems]);

    const fullSetSettingsColumns = useMemo(
        () =>
            columns.reduce((acc: ColDef[], column, idx) => {
                if (idx > 0 && !('hide' in column && column.hide)) {
                    const newColumn = { ...column } as ColDef & { disabled: boolean };

                    if (idx < 6) {
                        newColumn.disabled = true;
                    }

                    if (pinnedItems[getColumnId(column)]) {
                        newColumn.pinned = true;
                    }

                    acc.push(newColumn);
                }

                return acc;
            }, []),
        [columns, pinnedItems],
    );

    const onFullSetTableSettingsConfirm = useCallback(
        (visible) => {
            onTableSettingsConfirm({
                settingColumns: fullSetSettingsColumns,
                incomingColumns: visible,
                setSelection,
                setPinned,
            });
        },
        [fullSetSettingsColumns, setPinned, setSelection],
    );

    const isBtnDisabled = useMemo(
        () =>
            removeUnusedMtrStatus === RequestStatus.FETCHING ||
            projectData?.status === ProjectStatusEnum.Delayed ||
            projectData?.status === ProjectStatusEnum.Completed ||
            contractStatus !== ContractStatusEnum.InImplementation ||
            !priceListRules?.canDelete,
        [contractStatus, priceListRules?.canDelete, projectData?.status, removeUnusedMtrStatus],
    );

    const onGridReady = useCallback((event: GridReadyEvent) => {
        event.api.showNoRowsOverlay();
    }, []);

    useEffect(() => {
        if (tableSettingsRef.current) {
            setPosition(tableSettingsRef.current?.getBoundingClientRect().bottom);
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tableSettingsRef.current?.getBoundingClientRect().bottom, isHeaderOpened]);

    return (
        <ProjectPageLayout
            isHeaderCollapsable
            title="Прейскурант МТР"
            breadCrumbsItems={breadCrumbsMtrItems}
            headerBottom={
                <div className="wrapperToolbarLine">
                    <Layout>
                        <Button
                            label="Удалить неиспользуемые записи"
                            view="secondary"
                            size="s"
                            onClick={handleOpenConfirmationDialog}
                            disabled={isBtnDisabled}
                        />
                    </Layout>
                </div>
            }
        >
            <Layout direction="column">
                {projectData?.contractId && (
                    <>
                        <Grid cols={2} ref={tableSettingsRef}>
                            <GridItem>
                                <Grid cols={2} yAlign="center">
                                    <GridItem style={{ display: 'flex' }}>
                                        <PriceListTableCount totalCount={fetchedCount} totalItems={totalCount} />
                                    </GridItem>
                                </Grid>
                            </GridItem>
                            <GridItem style={{ display: 'flex', justifyContent: 'right' }}>
                                <TableSettings
                                    columns={fullSetSettingsColumns as any}
                                    onConfirm={onFullSetTableSettingsConfirm}
                                    isColumnVisible={isColumnVisible}
                                    showPinColumn
                                    tableApi={ref?.current}
                                />
                            </GridItem>
                        </Grid>
                        <div
                            className={cn('ag-theme-alpine', Styles.priceListMtr)}
                            style={
                                {
                                    '--tableStartPosition': `${position ?? 0}px`,
                                } as CSSProperties
                            }
                        >
                            <AgGridTable
                                ref={ref}
                                datasource={datasource}
                                columnDefs={fullSetColumns}
                                rowModelType="infinite"
                                maxConcurrentDatasourceRequests={1}
                                maxBlocksInCache={50}
                                cacheBlockSize={50}
                                onGridReady={onGridReady}
                                suppressCellFocus
                                context={{
                                    tableUniqueId: 'mtrList',
                                }}
                            />
                        </div>
                    </>
                )}
            </Layout>

            <Routes>
                <Route
                    path="/editMtr/:mtrId"
                    element={<EditMtrDialog updateMtr={updateMtrHandler} resetPageData={resetPageData} hideInformer />}
                />
            </Routes>

            <ConfirmationDialog
                textHeader="Удаление неиспользуемых записей"
                textConfirmation="Вы уверены, что хотите удалить записи Прейскуранта в статусах «Черновик» и «Новая запись», не используемые в Наряд-заказах и Актах КС-2?"
                confirmButtonText="Удалить"
                cancelButtonText="Не удалять"
                isOpened={isOpenConfirmationDialog}
                onSubmit={handleActionQuery}
                onClose={handleCloseConfirmationDialog}
            />
        </ProjectPageLayout>
    );
};

PriceListMtr.displayName = 'PriceList';
